'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { inviteAccountant } from '@/actions/account/invite-accountant';
import { getAccountantAccess } from '@/actions/account/get-accountant-access';
import { revokeAccountantAccess } from '@/actions/account/revoke-accountant-access';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { useTranslations } from '@/hooks/useTranslations';
import { Loader2, Mail, UserPlus, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

const InviteAccountantSchema = z.object({
  email: z.string().email({
    message: 'Invalid email address',
  }),
});

type AccountantAccess = {
  id: string;
  accountantId: string;
  name: string | null;
  email: string | null;
  grantedAt: Date;
};

export function InviteAccountantForm() {
  const t = useTranslations('AccountSettings');
  const { status, wrap } = useSubmitStatus();
  const [success, setSuccess] = useState<string | null>(null);
  const [accessList, setAccessList] = useState<AccountantAccess[]>([]);
  const [revoking, setRevoking] = useState<string | null>(null);

  const form = useForm<z.infer<typeof InviteAccountantSchema>>({
    resolver: zodResolver(InviteAccountantSchema),
    defaultValues: {
      email: '',
    },
  });

  useEffect(() => {
    getAccountantAccess().then(setAccessList);
  }, []);

  const onSubmit = async (values: z.infer<typeof InviteAccountantSchema>) => {
    setSuccess(null);
    wrap(async () => {
      const result = await inviteAccountant(values);
      if (result.error) {
        form.setError('email', { message: result.error });
        return;
      }
      if (result.success) {
        setSuccess(result.success);
        form.reset();
        // Refresh access list in case accountant was already registered
        getAccountantAccess().then(setAccessList);
      }
    });
  };

  const handleRevoke = async (accountantId: string) => {
    setRevoking(accountantId);
    const result = await revokeAccountantAccess(accountantId);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success(result.success);
      setAccessList((prev) =>
        prev.filter((a) => a.accountantId !== accountantId)
      );
    }
    setRevoking(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('inviteAccountant')}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t('inviteAccountantDescription')}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('accountantEmail')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="accountant@example.com"
                    disabled={status === 'loading'}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {success && (
            <Alert className="bg-green-50 border-green-200">
              <Mail className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {success}
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            disabled={status === 'loading'}
            className="w-full"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('sending')}
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                {t('sendInvitation')}
              </>
            )}
          </Button>
        </form>
      </Form>

      {accessList.length > 0 && (
        <div className="pt-4 border-t">
          <h4 className="text-sm font-semibold mb-3">
            {t('activeAccountants')}
          </h4>
          <div className="space-y-2">
            {accessList.map((access) => (
              <div
                key={access.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium">
                    {access.name || 'Unnamed'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {access.email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRevoke(access.accountantId)}
                  disabled={revoking === access.accountantId}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  {revoking === access.accountantId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
